import { useState, useEffect } from 'react';
import { DndContext, closestCenter, DragEndEvent, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import FireworksLayer from '@/components/interAction/FireworksLayer';
import DroppableArea from '@/components/interAction/Drag/DroppableArea'
import DraggableWord from '@/components/interAction/Drag/DragableWord'
import FlipCard from '@/components/interAction/FlipCard'
import * as styles from '@/components/organisms/Learning/LearningSytle.css'
import { getStudentActivityTitle } from '@/utils/activityTitle';

interface DiagramWord {
    id: string;
    category: string;
    value: string;
    label: string;
}

interface DiagramProps {
    activity: any;
    unitData?: any;
    onResponse?: (response: any) => void;
    existingResponse?: any;
    isHomework?: boolean;
}

export default function Diagram({ activity, unitData, onResponse, existingResponse, isHomework }: DiagramProps) {
    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: { distance: 5 },
        }),
        useSensor(TouchSensor, {
            activationConstraint: { delay: 120, tolerance: 8 },
        }),
    ); // [QA Fix][Row 186] 터치 센서 활성화로 모바일 드래그 지원

    const title = getStudentActivityTitle(activity?.title, '다이어그램');

    // activity.configJson에서 데이터 파싱
    let categories: string[] = [];
    let words: string[][] = [];
    let initialWords: DiagramWord[] = [];
    let correctAnswers: Record<string, string[]> = {};

    try {
        const config = typeof activity.configJson === 'string' 
            ? JSON.parse(activity.configJson) 
            : activity.configJson;

        categories = config.categories || [];
        words = config.words || [];

        console.log('=== Diagram 데이터 ===');
        console.log('카테고리:', categories);
        console.log('단어 그룹:', words);

        // words 배열을 평탄화하여 initialWords 생성
        words.forEach((wordGroup: string[], groupIndex: number) => {
            const category = categories[groupIndex];
            wordGroup.forEach((word: string, wordIndex: number) => {
                const normalizedWord = word.trim().toLowerCase();
                const uniqueId = `${normalizedWord}-${groupIndex}-${wordIndex}`;
                initialWords.push({
                    id: uniqueId,
                    category: category,
                    value: normalizedWord,
                    label: word
                });
            });
        });

        // correctAnswers 생성
        words.forEach((wordGroup: string[], groupIndex: number) => {
            const category = categories[groupIndex];
            correctAnswers[category] = wordGroup.map((w: string) => w.toLowerCase());
        });

        console.log('초기 단어 목록:', initialWords);
        console.log('정답:', correctAnswers);
    } catch (e) {
        console.error('Diagram configJson 파싱 에러:', e);
    }

    const wordInfoMap = initialWords.reduce<Record<string, DiagramWord>>((acc, word) => {
        acc[word.id] = word;
        return acc;
    }, {});

    const valueToIdsMap = initialWords.reduce<Record<string, string[]>>((acc, word) => {
        if (!acc[word.value]) {
            acc[word.value] = [];
        }
        acc[word.value].push(word.id);
        return acc;
    }, {});

    const categoryValueToIdsMap = categories.reduce<Record<string, Record<string, string[]>>>((acc, category) => {
        acc[category] = {};
        return acc;
    }, {});

    initialWords.forEach((word) => {
        if (!categoryValueToIdsMap[word.category][word.value]) {
            categoryValueToIdsMap[word.category][word.value] = [];
        }
        categoryValueToIdsMap[word.category][word.value].push(word.id);
    });

    const normalizeExistingResponse = (response?: any) => {
        if (!response) return undefined;

        const allIds = [
            ...(response.pool || []),
            ...Object.values(response.wordLocations || {}).flat()
        ];

        const hasLegacyIds = allIds.some((wordId: string) => wordId && !wordInfoMap[wordId]);
        if (!hasLegacyIds) {
            return response;
        }

        const usedIds = new Set<string>();
        const convertWordId = (wordId: string, category?: string) => {
            if (wordInfoMap[wordId]) {
                usedIds.add(wordId);
                return wordId;
            }

            const normalized = wordId?.toLowerCase();
            const categoryCandidates = category ? categoryValueToIdsMap[category]?.[normalized] || [] : [];
            const generalCandidates = valueToIdsMap[normalized] || [];

            const availableId = [...categoryCandidates, ...generalCandidates].find((id) => !usedIds.has(id));
            if (availableId) {
                usedIds.add(availableId);
                return availableId;
            }

            return wordId;
        };

        const convertList = (list: string[] = [], category?: string) =>
            list.map((wordId) => convertWordId(wordId, category));

        const convertedLocations = Object.keys(response.wordLocations || {}).reduce<Record<string, string[]>>((acc, category) => {
            acc[category] = convertList(response.wordLocations?.[category] || [], category);
            return acc;
        }, {});

        const convertedPool = convertList(response.pool || []);

        return {
            ...response,
            wordLocations: convertedLocations,
            pool: convertedPool,
        };
    };

    const normalizedResponse = normalizeExistingResponse(existingResponse);

const sanitizeWordLabel = (word: string) => word?.replace(/-\d+-\d+$/, '') ?? '';

const getWordValue = (wordId: string) => {
        if (!wordId) return '';
        return wordInfoMap[wordId]?.value ?? sanitizeWordLabel(wordId).toLowerCase();
    };

    const getWordLabel = (wordId: string) => {
        if (!wordId) return '';
        const raw =
            wordInfoMap[wordId]?.label ??
            wordInfoMap[wordId]?.value ??
            wordId;
        return sanitizeWordLabel(raw);
    };

    type Category = string;

    // 동적으로 초기 상태 생성
    const getInitialState = () => {
        const initialLocations: Record<string, string[]> = {};
        const initialFlipStatus: Record<string, 'idle' | 'success' | 'flippedBack'> = {};

        categories.forEach(cat => {
            initialLocations[cat] = [];
            initialFlipStatus[cat] = 'idle';
        });

        // existingResponse에서 이전 상태 복원
        if (normalizedResponse?.wordLocations) {
            Object.keys(normalizedResponse.wordLocations).forEach(cat => {
                initialLocations[cat] = [...normalizedResponse.wordLocations[cat]];
            });
        }

        return {
            locations: initialLocations,
            flipStatus: initialFlipStatus,
            pool: normalizedResponse?.pool ? [...normalizedResponse.pool] : initialWords.map((w) => w.id),
            completedCategories: normalizedResponse?.completedCategories || []
        };
    };

    const initialState = getInitialState();

    const [wordLocations, setWordLocations] = useState<Record<Category, string[]>>(initialState.locations);
    const [pool, setPool] = useState<string[]>(initialState.pool);
    const [completedCategories, setCompletedCategories] = useState<Category[]>(initialState.completedCategories);
    const [flipStatus, setFlipStatus] = useState<Record<Category, 'idle' | 'success' | 'flippedBack'>>(initialState.flipStatus);


// 컴포넌트 마운트 시 초기 응답 저장 또는 기존 데이터 복원
// eslint-disable-next-line react-hooks/exhaustive-deps
useEffect(() => {
    if (normalizedResponse && normalizedResponse.wordLocations && Object.keys(normalizedResponse.wordLocations).length > 0) {
      console.log('Diagram: 기존 응답 데이터 복원 및 저장');
      if (onResponse) {
        onResponse({
          ...normalizedResponse,
          isHomeworkReady: Array.isArray(normalizedResponse.pool) ? normalizedResponse.pool.length === 0 : false
        });
      }
    } else if (onResponse) {
      // 초기 응답 저장 (모든 카테고리가 비어있음 = 미완료)
      console.log('Diagram: 초기 응답 저장');
      const initialLocations: Record<string, string[]> = {};
      categories.forEach(cat => {
        initialLocations[cat] = [];
      });
      onResponse({
        wordLocations: initialLocations,
        pool: initialWords.map((w) => w.id),
        completedCategories: [],
        isCorrect: false,
        isHomeworkReady: false
      });
    }
  }, []);

useEffect(() => {
    categories.forEach((category) => {
      const isComplete = checkCategoryCompletion(category, wordLocations[category]);
  
      // 숙제인 경우 피드백 표시 안 함
      if (!isHomework) {
        // ✅ 1. 정답일 때, 최초 한 번만 success → flippedBack
        if (isComplete && flipStatus[category] === 'idle') {
          setFlipStatus((prev) => ({ ...prev, [category]: 'success' }));
    
          setTimeout(() => {
            setFlipStatus((prev) => ({ ...prev, [category]: 'flippedBack' }));
            if (!completedCategories.includes(category)) {
              setCompletedCategories((prev) => [...prev, category]);
            }
          }, 2000);
        }
    
        // ✅ 2. 정답이 아닌데 현재 flip 중이면 idle로 초기화
        if (!isComplete && flipStatus[category] !== 'idle') {
          setFlipStatus((prev) => ({ ...prev, [category]: 'idle' }));
          setCompletedCategories((prev) => prev.filter((c) => c !== category));
        }
      }
    });
  }, [wordLocations, isHomework]);

const findCurrentLocation = (wordId: string): Category | 'Pool' => {
    for (const category of categories) {
        if (wordLocations[category].includes(wordId)) {
            return category;
        }
    }
    if (pool.includes(wordId)) {
        return 'Pool';
    }
    return 'Pool';
};

const moveWord = (wordId: string, to: Category | 'Pool') => {
    if (to === 'Pool') {
        setPool((prev) => (!prev.includes(wordId) ? [...prev, wordId] : prev));
        setWordLocations((prev) => {
            const newLocations = { ...prev };
            for (const key of categories) {
                newLocations[key] = newLocations[key].filter((id) => id !== wordId);
            }
            return newLocations;
        });
    } else {
        setWordLocations((prev) => {
            const newLocations = { ...prev };
            for (const key of categories) {
                newLocations[key] = newLocations[key].filter((id) => id !== wordId);
            }
            newLocations[to].push(wordId);
            return newLocations;
        });
        setPool((prev) => prev.filter((id) => id !== wordId));
    }
};

const checkCategoryCompletion = (category: Category, words: string[]) => {
    const correct = correctAnswers[category] || [];
    const sortedCurrent = words.map((wordId) => getWordValue(wordId)).sort();
    const sortedCorrect = [...correct].map((word) => word.toLowerCase()).sort();
    return sortedCurrent.length === sortedCorrect.length && sortedCurrent.every((word, index) => word === sortedCorrect[index]);
};

const handleDragEnd = (event: DragEndEvent) => {
    const { active, over, delta } = event;
    if (!over) return;

    // 👇 클릭만 했을 때는 이동 X
    if (Math.abs(delta.x) < 5 && Math.abs(delta.y) < 5) return;

    const wordId = active.id.toString();
    const overId = over.id.toString() as Category | 'Pool';
    const previousLocation = findCurrentLocation(wordId);

    moveWord(wordId, overId);

    setTimeout(() => {
        let updatedCompletedCategories = [...completedCategories];

        if (overId !== 'Pool') {
            const newWords = wordLocations[overId as Category].filter((id) => id !== wordId).concat(wordId);
            if (checkCategoryCompletion(overId as Category, newWords)) {
                if (!completedCategories.includes(overId as Category)) {
                    updatedCompletedCategories = [...updatedCompletedCategories, overId as Category];
                    setCompletedCategories(updatedCompletedCategories);
                }
            } else {
                if (completedCategories.includes(overId as Category)) {
                    updatedCompletedCategories = updatedCompletedCategories.filter((cat) => cat !== overId);
                    setCompletedCategories(updatedCompletedCategories);
                }
            }
        }

        if (previousLocation !== 'Pool') {
            const prevWords = wordLocations[previousLocation as Category].filter((id) => id !== wordId);
            if (checkCategoryCompletion(previousLocation as Category, prevWords)) {
                if (!completedCategories.includes(previousLocation as Category)) {
                    updatedCompletedCategories = [...updatedCompletedCategories, previousLocation as Category];
                    setCompletedCategories(updatedCompletedCategories);
                }
            } else {
                if (completedCategories.includes(previousLocation as Category)) {
                    updatedCompletedCategories = updatedCompletedCategories.filter((cat) => cat !== previousLocation);
                    setCompletedCategories(updatedCompletedCategories);
                }
            }
        }

        // 상태 저장
        if (onResponse) {
            const updatedLocations = { ...wordLocations };
            if (overId !== 'Pool') {
                updatedLocations[overId] = [...updatedLocations[overId].filter((id) => id !== wordId), wordId];
            } else {
                categories.forEach(cat => {
                    updatedLocations[cat] = updatedLocations[cat].filter((id) => id !== wordId);
                });
            }

            const updatedPool = overId === 'Pool' 
                ? [...pool.filter((id) => id !== wordId), wordId]
                : pool.filter((id) => id !== wordId);

            // 모든 카테고리가 완료되었는지 확인
            const allCategoriesCompleted = categories.every(cat => 
                updatedCompletedCategories.includes(cat)
            );

            onResponse({
                wordLocations: updatedLocations,
                pool: updatedPool,
                completedCategories: updatedCompletedCategories,
                isCorrect: allCategoriesCompleted,
                isHomeworkReady: updatedPool.length === 0
            });
        }
    }, 0);
};

  return (
    <main>
     <div>
        <h1 className='px-5 pt-5 pb-3 heading1'>{title}</h1>
        <div className='p-5 pt-0'>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <div className={styles.categoryGrid}>
                    {categories.map((category) => (
                        <div key={category} className='flex flex-col'>
                            <div className='mb-3 text-sm font-bold'>{category}</div>
                                <FlipCard
                                className='h-full *:h-full'
                                    messageClassName="!text-[1rem] text-left"
                                    flipStatus={!isHomework && flipStatus[category] === 'success' ? 'success' : 'idle'} // 숙제는 피드백 안 함
                                    front={
                                    <DroppableArea id={category} 
                                    isCompleted={!isHomework && completedCategories.includes(category)} 
                                    className='h-full border border-gray-100 min-h-44' 
                                    >
                                     {(wordLocations[category] ?? [])?.length > 0 ? (
                                        (wordLocations[category] ?? []).map((wordId) => (
                                        <DraggableWord
                                            key={wordId}
                                            id={wordId}
                                            label={getWordLabel(wordId)}
                                            initialWords={initialWords}
                                            currentCategory={category}
                                            className="!text-lg"
                                            isHomework={isHomework}
                                        />
                                        ))
                                    ) : (
                                        <div className="text-sm text-center text-gray-400">
                                        카테고리에 맞는 <br /> 
                                        단어를 넣어주세요!
                                        </div>
                                    )}
                                    </DroppableArea>}
                                />
                        </div>
                    ))}
                </div>
                
                
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
                    <div className='text-sm font-bold'>{'Pool'}</div>
                    <DroppableArea id="Pool" isCompleted={false} className='min-h-32'>
                        {pool.map((wordId) => (
                            <DraggableWord 
                                key={wordId} 
                                id={wordId} 
                                label={getWordLabel(wordId)}
                                initialWords={initialWords} 
                                currentCategory="Pool" 
                                className='!text-lg' 
                                isHomework={isHomework}
                            />
                        ))}
                    </DroppableArea>
                </div>
          </DndContext>
        </div>
     </div>
    </main>
  )
}